/**
 * 认证服务 - 后端 API 对接服务层
 *
 * 基于 VITE_USE_MOCK 控制：VITE_USE_MOCK=true 时使用 Mock；
 * 默认调用真实后端接口，Mock 仅用于开发回退。
 */

import { hasAdminRole, type LoginParams, type LoginResult, type SendCodeResult, type UserAccount } from '@/types/auth'
import { httpClient } from '@/utils/httpClient'
import { extractErrorMessage } from '@/utils/errorUtils'
import { ApiError } from '@/utils/apiError'
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from '@/utils/tokenManager'

/** 是否使用后端 API（基于 VITE_USE_MOCK：不启用 Mock 时调用后端，默认走真实 API） */
const USE_BACKEND_API = import.meta.env.VITE_USE_MOCK !== 'true'

/**
 * 修复登录审查#5：登录/发码/重置失败的「真因」透出。
 * 拦截器对 HTTP200+业务码≠200 抛 ApiError（无 .response，原实现
 * error.response?.data?.message 恒取不到）；且业务码（4006 验证码过期、
 * 4007 验证码错误、403 账号已禁用等）超出 HTTP 状态区间，classifyError 按
 * 数值分会误归入「服务器错误（4007）」，故业务错误直接透出后端 message，
 * 其余错误（网络/HTTP 层）仍走 extractErrorMessage 分类文案。
 */
function extractAuthError(error: unknown, fallback: string): string {
  if (error instanceof ApiError && error.code > 0) {
    return error.message || fallback
  }
  return extractErrorMessage(error, fallback)
}

export interface IAuthService {
  sendCode(mobile: string): Promise<SendCodeResult>
  login(params: LoginParams): Promise<LoginResult>
  refreshToken(refreshToken: string): Promise<{ accessToken: string; refreshToken: string }>
  logout(): Promise<void>
  adminResetPassword(mobile: string, code: string, newPassword: string): Promise<{ success: boolean; message?: string }>
  getCurrentUser(): Promise<UserAccount | null>
}

export const authService: IAuthService = {
  async sendCode(mobile: string): Promise<SendCodeResult> {
    if (!USE_BACKEND_API) {
      const { sendVerificationCode: mockSendCode } = await import('@/mock/auth')
      return mockSendCode(mobile)
    }
    try {
      await httpClient.post('/api/auth/send-code', { mobile })
      return { success: true, message: '验证码已发送' }
    } catch (error) {
      // 修复登录审查#5：统一 extractAuthError 透出「验证码错误/账号已禁用」等真因
      return {
        success: false,
        error: extractAuthError(error, '验证码发送失败'),
      }
    }
  },

  async login(params: LoginParams): Promise<LoginResult> {
    if (!USE_BACKEND_API) {
      const { login: mockLogin } = await import('@/mock/auth')
      return mockLogin(params)
    }

    try {
      let response
      if (params.method === 'mobile_code') {
        response = await httpClient.post('/api/auth/login', {
          mobile: params.mobile,
          code: params.code,
        })
      } else if (params.method === 'password') {
        response = await httpClient.post('/api/auth/admin/login', {
          mobile: params.username,
          password: params.password,
        })
      } else {
        // 微信登录：code + state 透传给后端；state 由 WechatLoginCallbackView 解析 URL 后回填
        const wechatBody: { authCode: string; state?: string } = { authCode: params.authCode }
        if ('state' in params && params.state) {
          wechatBody.state = params.state
        }
        response = await httpClient.post('/api/auth/wechat/callback', wechatBody)
      }

      // httpClient 拦截器已将 response.data 解包为后端 data 字段
      // 修复登录审查#11：requirePasswordChange 是登录响应顶层字段（非 user 内字段），
      // 原实现从 user.requirePasswordChange 取值恒为 undefined，强制改密标记形同虚设
      const {
        accessToken,
        refreshToken,
        expiresIn,
        user,
        pendingDeletion,
        requirePasswordChange,
      } = response.data

      // Save tokens
      setTokens(accessToken, refreshToken, expiresIn)

      return {
        success: true,
        user: {
          id: String(user.id),
          mobile: user.mobile,
          displayName: user.displayName,
          avatarUrl: user.avatarUrl || undefined,
          organizationId: user.organizationId,
          organizationName: user.organizationName,
          organizationType: user.organizationType,
          region: user.region,
          role: user.role,
          permissions: user.permissions,
          isFirstLogin: user.isNewUser,
          forcePasswordChange: requirePasswordChange,
          createdAt: new Date(),
          lastLoginAt: new Date(),
        },
        token: accessToken,
        refreshToken,
        expiresIn,
        // 冷静期用户允许登录（便于撤销注销），登录后直接引导到注销状态页而非常规工作台
        redirectTo:
          pendingDeletion
            ? '/settings/danger'
            : hasAdminRole(user.role)
              ? '/admin'
              : user.requireSetup
                ? undefined
                : '/dashboard',
        requireSetup: user.requireSetup,
      }
    } catch (error) {
      // 修复登录审查#5：同上，统一 extractAuthError
      return {
        success: false,
        error: extractAuthError(error, '登录失败'),
      }
    }
  },

  async refreshToken(
    refreshToken: string,
  ): Promise<{ accessToken: string; refreshToken: string }> {
    const response = await httpClient.post('/api/auth/refresh', {
      refreshToken,
    })
    return response.data
  },

  async logout(): Promise<void> {
    if (!USE_BACKEND_API) {
      const { logout: mockLogout } = await import('@/mock/auth')
      const token = getAccessToken()
      if (token) await mockLogout(token)
      return
    }
    try {
      // 设备管理 P0-2:上送 refreshToken,后端吊销其 token family,防止登出后仍可刷新会话
      const refreshToken = getRefreshToken()
      await httpClient.post('/api/auth/logout', refreshToken ? { refreshToken } : {})
    } finally {
      clearTokens()
    }
  },

  async adminResetPassword(
    mobile: string,
    code: string,
    newPassword: string,
  ): Promise<{ success: boolean; message?: string }> {
    try {
      await httpClient.post('/api/auth/admin/reset-password', {
        mobile,
        code,
        newPassword,
      })
      return { success: true, message: '密码重置成功' }
    } catch (error) {
      // 修复登录审查#5：同上，统一 extractAuthError
      return {
        success: false,
        message: extractAuthError(error, '密码重置失败'),
      }
    }
  },

  async getCurrentUser(): Promise<UserAccount | null> {
    if (!USE_BACKEND_API) return null
    try {
      const response = await httpClient.get('/api/auth/me')
      // httpClient 拦截器已将 response.data 解包为后端 data 字段
      const user = response.data
      return {
        id: String(user.id),
        mobile: user.mobile,
        displayName: user.displayName,
        avatarUrl: user.avatarUrl || undefined,
        organizationId: user.organizationId,
        organizationName: user.organizationName,
        organizationType: user.organizationType,
        region: user.region,
        role: user.role,
        permissions: user.permissions,
        isFirstLogin: user.isNewUser,
        forcePasswordChange: user.requirePasswordChange,
        createdAt: new Date(user.createdAt || Date.now()),
        lastLoginAt: new Date(user.lastLoginAt || Date.now()),
      }
    } catch {
      return null
    }
  },
}
